import React from "react";

import { useTranslation } from "react-i18next";

import { useAppState } from "../../../../../../context/StateContext";
import { CartonView } from "../../../../../../models/carton";
import StyledBadge from "../../../../StyledBadge";
import useStyles from "./styles";

interface Props {
  carton?: CartonView;
  isBookingCancelled?: boolean;
  showOpenStatus?: boolean;
}

interface StatusOptions {
  background?: string;
  color?: string;
  translateKey?: string;
}
enum Color {
  SUCCESS = "success",
  WARNING = "warning",
  CANCELLED = "cancelled",
}

const StatusBadge = (props: Props) => {
  const { carton, isBookingCancelled, showOpenStatus } = props;

  const { t: translate } = useTranslation();
  const { isFMVersion } = useAppState();
  const classes = useStyles();

  if (!carton) return null;

  const cartonIsCancelled = (carton?: CartonView) => {
    if (!carton) return false;

    return isBookingCancelled || carton?.isCancelled;
  };

  const getStatusOptions = (translate: string, color: Color) => {
    switch (color) {
      case Color.SUCCESS:
        return {
          background: "#ECFDF3",
          color: "#039855",
          translateKey: translate,
        };
      case Color.CANCELLED:
        return {
          background: "#FEE4E2",
          color: "#F04438",
          translateKey: translate,
        };
      default:
        return { background: "#FFFAEB", color: "#DC6803", translateKey: translate };
    }
  };

  const getCartonStatus = () => {
    if (cartonIsCancelled(carton)) {
      return getStatusOptions("cancelled", Color.CANCELLED);
    }
    if (isFMVersion && carton.isReceived) {
      return getStatusOptions("received", Color.SUCCESS);
    }

    if (isFMVersion && !showOpenStatus) {
      const { isReceivedByCfs } = carton;

      return getStatusOptions(
        isReceivedByCfs ? "receivedByCFS" : "notReceived",
        isReceivedByCfs ? Color.SUCCESS : Color.WARNING
      );
    }

    if (!isFMVersion || showOpenStatus) {
      return getStatusOptions(
        carton.isClosed ? "closed" : "ssccOpen",
        carton.isClosed ? Color.SUCCESS : Color.WARNING
      );
    }

    return getStatusOptions("ssccOpen", Color.WARNING);
  };

  const options: StatusOptions = getCartonStatus();

  return (
    <StyledBadge
      backgroundColor={options.background}
      textColor={options.color}
      value={translate(options?.translateKey || "")}
      className={classes.badgeContent}
    />
  );
};

export default StatusBadge;
